// lib/api-client.ts
import axios, { AxiosError } from 'axios';
import { initCsrf, getCsrfToken } from '@/utils/csrf';

const getBaseUrl = () => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  // Always use a relative URL so requests go through the Vite proxy in dev
  // and through Nginx in production — keeping everything same-origin.
  return '/api/v1';
};

export const apiClient = axios.create({
  baseURL: getBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for session cookies and CSRF
});

// On a 403, refresh the CSRF token and retry the request exactly once.
// This handles the case where a user leaves a tab open long enough for
// the server-side session to expire, invalidating the old token.
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _csrfRetry?: boolean };

    if (error.response?.status === 403 && !originalRequest._csrfRetry) {
      originalRequest._csrfRetry = true;

      await initCsrf();
      const newToken = getCsrfToken();
      if (newToken && originalRequest.headers) {
        originalRequest.headers['X-CSRFToken'] = newToken;
      }

      return apiClient(originalRequest);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
